import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCalendarEvent,
  IconCheck,
  IconCloudUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconPlus,
  IconScissors,
  IconTrash,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { manualRevenueService } from "../../services/manualRevenue";
import "./RevenueImportPage.css";

// Import Revenue: record flat revenue figures for reps — a CSV of season
// totals, or one figure at a time — and carve dated slices out of a bulk
// figure so overrides that only apply to part of the season see the right
// amount. Talks to /v1/manual-revenue/*.

const STEPS = [
  {
    icon: IconFileSpreadsheet,
    title: "Fill in the sheet",
    body: "One row per figure: Rep ID or Email and an Amount. Dates are optional.",
  },
  {
    icon: IconCloudUpload,
    title: "Upload it",
    body: "Any bad row rejects the whole file with the row number, so fix and re-upload once.",
  },
  {
    icon: IconScissors,
    title: "Date the slices that matter",
    body: "Undated revenue counts for the season only. Carve out the part that happened inside an override's dates.",
  },
];

const money = n =>
  Number(n ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const dateRange = (start, end) =>
  start && end ? (start === end ? start : `${start} → ${end}`) : "Undated";

const EMPTY_TIERS = {
  net_amount: "",
  serviced_amount: "",
  qualified_amount: "",
};

const EMPTY_ENTRY = {
  rep_id: null,
  amount: "",
  ...EMPTY_TIERS,
  accounts: "",
  start_date: "",
  end_date: "",
  note: "",
};

const EMPTY_SLICE = {
  amount: "",
  ...EMPTY_TIERS,
  accounts: "",
  start_date: "",
  end_date: "",
};

const TIER_FIELDS = [
  { key: "net_amount", label: "Net", follows: "amount" },
  { key: "serviced_amount", label: "Serviced", follows: "net" },
  { key: "qualified_amount", label: "Qualified", follows: "serviced" },
];

/** The tier fields the user actually filled in, as numbers, for the API. */
const tierPayload = form =>
  Object.fromEntries(
    TIER_FIELDS.filter(t => form[t.key] !== "" && form[t.key] != null).map(
      t => [t.key, Number(form[t.key])]
    )
  );

/**
 * Mirrors the API's cascade: a blank tier follows the tier above it, and no
 * stated tier may exceed the one above. Returns a message or null.
 */
const tierOrderMessage = form => {
  const gross = Number(form.amount);
  const stated = k =>
    form[k] === "" || form[k] == null ? null : Number(form[k]);
  const net = stated("net_amount") ?? gross;
  const serviced = stated("serviced_amount") ?? net;
  const qualified = stated("qualified_amount") ?? serviced;
  if (stated("net_amount") != null && net > gross)
    return "Net cannot exceed the amount";
  if (stated("serviced_amount") != null && serviced > net)
    return "Serviced cannot exceed net";
  if (stated("qualified_amount") != null && qualified > serviced)
    return "Qualified cannot exceed serviced";
  return null;
};

/** "Net $800 · Serviced $500 · Qualified $100", or null when all equal gross. */
const tierSummary = entry => {
  const t = entry.tiers;
  if (!t) return null;
  if (t.net === t.gross && t.serviced === t.gross && t.qualified === t.gross)
    return null;
  return `Net ${money(t.net)} · Serviced ${money(t.serviced)} · Qualified ${money(t.qualified)}`;
};

function RevenueImportPage() {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [error, setError] = useState(null);
  const [reps, setReps] = useState([]);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const fileInputRef = useRef(null);

  const [draft, setDraft] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);

  // Which bulk entry has its "add dated slice" form open, and that form.
  const [slicingId, setSlicingId] = useState(null);
  const [slice, setSlice] = useState(EMPTY_SLICE);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const data = await manualRevenueService.list(jwt);
      setEntries(data.entries || []);
      setTotals(data.totals || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  const loadReps = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint("/v1/rep/reps-list"), {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const payload = await response.json().catch(() => ({}));
      const users = payload?.data?.users || [];
      setReps(
        users
          .map(u => ({
            value: String(u.rep_id),
            label: `${u.first_name} ${u.last_name}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch {
      // The picker is a convenience; the CSV path still works without it.
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    load();
    loadReps();
  }, [load, loadReps]);

  // ---------------------------------------------------------------------
  // CSV
  // ---------------------------------------------------------------------

  const pickFile = list => {
    const chosen = list && list[0];
    if (!chosen) return;
    if (!/\.csv$/i.test(chosen.name)) {
      setError("Choose a .csv file");
      return;
    }
    setError(null);
    setFile(chosen);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setLastImport(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const data = await manualRevenueService.importCsv(file, jwt);
      setLastImport(data);
      setFile(null);
      showSuccess(
        `Imported ${data.created} revenue ${data.created === 1 ? "row" : "rows"}`
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleTemplate = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const text = await manualRevenueService.template(jwt);
      const blob = new Blob([text], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "revenue-import-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  // ---------------------------------------------------------------------
  // Single entry / slices / delete
  // ---------------------------------------------------------------------

  const handleCreate = async () => {
    if (!draft.rep_id || !draft.amount) {
      setError("Pick a rep and enter an amount");
      return;
    }
    if (!!draft.start_date !== !!draft.end_date) {
      setError("Give both a start and an end date, or neither");
      return;
    }
    const tierError = tierOrderMessage(draft);
    if (tierError) {
      setError(tierError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      await manualRevenueService.create(
        {
          rep_id: Number(draft.rep_id),
          amount: Number(draft.amount),
          ...tierPayload(draft),
          ...(draft.accounts !== ""
            ? { accounts: Number(draft.accounts) }
            : {}),
          ...(draft.start_date
            ? { start_date: draft.start_date, end_date: draft.end_date }
            : {}),
          ...(draft.note ? { note: draft.note } : {}),
        },
        jwt
      );
      showSuccess("Revenue recorded");
      setDraft(EMPTY_ENTRY);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSlice = async entry => {
    if (!slice.amount || !slice.start_date || !slice.end_date) {
      setError("A dated slice needs an amount, a start date and an end date");
      return;
    }
    const sliceTierError = tierOrderMessage(slice);
    if (sliceTierError) {
      setError(sliceTierError);
      return;
    }
    setBusyId(entry.id);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      await manualRevenueService.allocate(
        entry.id,
        {
          amount: Number(slice.amount),
          ...tierPayload(slice),
          ...(slice.accounts !== ""
            ? { accounts: Number(slice.accounts) }
            : {}),
          start_date: slice.start_date,
          end_date: slice.end_date,
        },
        jwt
      );
      showSuccess("Dated slice added");
      setSlicingId(null);
      setSlice(EMPTY_SLICE);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async entry => {
    const what =
      entry.parent_entry_id != null
        ? "this dated slice"
        : `${money(entry.amount)} for ${entry.first_name} ${entry.last_name}` +
          (entry.allocations?.length ? " and its dated slices" : "");
    if (!window.confirm(`Delete ${what}?`)) return;
    setBusyId(entry.id);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      await manualRevenueService.remove(entry.id, jwt);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  const headerActions = (
    <Button
      variant="outline"
      leftSection={<IconArrowLeft size={16} />}
      onClick={() => navigate("/downline")}
    >
      Back to Reps
    </Button>
  );

  const renderSteps = () => (
    <div className="revenue-import-page-steps">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        return (
          <div key={step.title} className="revenue-import-page-step">
            <span className="revenue-import-page-step-number">{index + 1}</span>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Icon size={16} color="var(--mantine-color-orange-7)" />
                <Text fw={600} size="sm">
                  {step.title}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {step.body}
              </Text>
            </Stack>
          </div>
        );
      })}
    </div>
  );

  const renderUploader = () => (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={700}>Upload a revenue sheet</Text>
            <Text size="sm" c="dimmed">
              Columns: Rep ID or Email, Amount, then optional Net Amount,
              Serviced Amount, Qualified Amount, Accounts, Start Date, End Date,
              Note, Parent Entry ID.
            </Text>
          </div>
          <Button
            variant="subtle"
            leftSection={<IconDownload size={16} />}
            onClick={handleTemplate}
          >
            Download template
          </Button>
        </Group>

        <div
          className={[
            "revenue-import-page-dropzone",
            dragging ? "revenue-import-page-dropzone-active" : "",
            uploading ? "revenue-import-page-dropzone-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            if (!dragging) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            if (!uploading) pickFile(e.dataTransfer?.files);
          }}
        >
          <span className="revenue-import-page-dropzone-icon">
            <IconCloudUpload size={28} />
          </span>
          <Text fw={700}>
            {file
              ? file.name
              : dragging
                ? "Drop the CSV here"
                : "Drag & drop a CSV"}
          </Text>
          <Text size="sm" c="dimmed">
            or{" "}
            <Text span c="orange.7" fw={600}>
              browse your files
            </Text>
          </Text>
          <input
            ref={fileInputRef}
            className="revenue-import-page-dropzone-input"
            type="file"
            accept=".csv,text/csv"
            disabled={uploading}
            onChange={e => {
              pickFile(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        <Group justify="flex-end">
          {file && (
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setFile(null)}
              disabled={uploading}
            >
              Clear
            </Button>
          )}
          <Button
            leftSection={<IconCloudUpload size={16} />}
            onClick={handleUpload}
            disabled={!file}
            loading={uploading}
          >
            Import revenue
          </Button>
        </Group>

        {lastImport && (
          <Alert
            icon={<IconCheck size={16} />}
            color="teal"
            variant="light"
            withCloseButton
            onClose={() => setLastImport(null)}
          >
            Imported {lastImport.created}{" "}
            {lastImport.created === 1 ? "row" : "rows"}. They're in the list
            below; use the scissors on a row to date part of it.
          </Alert>
        )}
      </Stack>
    </Card>
  );

  const renderSingleEntry = () => (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <div>
          <Text fw={700}>Record one figure</Text>
          <Text size="sm" c="dimmed">
            Leave the dates blank for a season total; add them when you know
            exactly when the revenue happened. Net, serviced and qualified are
            optional: a blank one follows the tier above it.
          </Text>
        </div>
        <div className="revenue-import-page-form">
          <Select
            label="Rep"
            placeholder="Pick a rep"
            data={reps}
            searchable
            value={draft.rep_id}
            onChange={value => setDraft(d => ({ ...d, rep_id: value }))}
            className="revenue-import-page-form-rep"
          />
          <NumberInput
            label="Amount"
            placeholder="0"
            prefix="$"
            thousandSeparator=","
            min={0}
            value={draft.amount}
            onChange={value => setDraft(d => ({ ...d, amount: value }))}
          />
          {TIER_FIELDS.map(t => (
            <NumberInput
              key={t.key}
              label={t.label}
              placeholder={`= ${t.follows}`}
              prefix="$"
              thousandSeparator=","
              min={0}
              value={draft[t.key]}
              onChange={value => setDraft(d => ({ ...d, [t.key]: value }))}
            />
          ))}
          <NumberInput
            label="Accounts"
            placeholder="optional"
            min={0}
            allowDecimal={false}
            value={draft.accounts}
            onChange={value => setDraft(d => ({ ...d, accounts: value }))}
          />
          <TextInput
            label="Start date"
            type="date"
            value={draft.start_date}
            onChange={e =>
              setDraft(d => ({ ...d, start_date: e.target.value }))
            }
          />
          <TextInput
            label="End date"
            type="date"
            value={draft.end_date}
            onChange={e => setDraft(d => ({ ...d, end_date: e.target.value }))}
          />
          <TextInput
            label="Note"
            placeholder="optional"
            value={draft.note}
            onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
            className="revenue-import-page-form-note"
          />
        </div>
        <Group justify="flex-end">
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
            loading={saving}
          >
            Record revenue
          </Button>
        </Group>
      </Stack>
    </Card>
  );

  const renderSliceForm = entry => (
    <Table.Tr className="revenue-import-page-slice-row">
      <Table.Td colSpan={8}>
        <div className="revenue-import-page-slice-form">
          <Text size="sm" fw={600}>
            Of {money(entry.amount)}, how much happened when?
            {entry.unallocated_amount != null && (
              <Text span c="dimmed" fw={400}>
                {" "}
                ({money(entry.unallocated_amount)} still undated)
              </Text>
            )}
          </Text>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <NumberInput
              label="Amount"
              prefix="$"
              thousandSeparator=","
              min={0}
              value={slice.amount}
              onChange={value => setSlice(s => ({ ...s, amount: value }))}
              size="xs"
              w={140}
            />
            {TIER_FIELDS.map(t => (
              <NumberInput
                key={t.key}
                label={t.label}
                placeholder={`= ${t.follows}`}
                prefix="$"
                thousandSeparator=","
                min={0}
                value={slice[t.key]}
                onChange={value => setSlice(s => ({ ...s, [t.key]: value }))}
                size="xs"
                w={120}
              />
            ))}
            <NumberInput
              label="Accounts"
              placeholder="optional"
              min={0}
              allowDecimal={false}
              value={slice.accounts}
              onChange={value => setSlice(s => ({ ...s, accounts: value }))}
              size="xs"
              w={110}
            />
            <TextInput
              label="Start"
              type="date"
              value={slice.start_date}
              onChange={e =>
                setSlice(s => ({ ...s, start_date: e.target.value }))
              }
              size="xs"
            />
            <TextInput
              label="End"
              type="date"
              value={slice.end_date}
              onChange={e =>
                setSlice(s => ({ ...s, end_date: e.target.value }))
              }
              size="xs"
            />
            <Button
              size="xs"
              onClick={() => handleSlice(entry)}
              loading={busyId === entry.id}
            >
              Add slice
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => {
                setSlicingId(null);
                setSlice(EMPTY_SLICE);
              }}
            >
              Cancel
            </Button>
          </Group>
        </div>
      </Table.Td>
    </Table.Tr>
  );

  const renderRow = (entry, isSlice = false) => (
    <Table.Tr
      key={entry.id}
      className={isSlice ? "revenue-import-page-row-slice" : undefined}
    >
      <Table.Td>
        {isSlice ? (
          <Text size="sm" c="dimmed" pl="md">
            ↳ dated slice
          </Text>
        ) : (
          <Text size="sm" fw={600}>
            {entry.first_name} {entry.last_name}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{money(entry.amount)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {tierSummary(entry) ?? "= amount"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{entry.accounts ?? "—"}</Text>
      </Table.Td>
      <Table.Td>
        <Badge
          variant="light"
          color={entry.start_date ? "orange" : "gray"}
          leftSection={
            entry.start_date ? <IconCalendarEvent size={12} /> : null
          }
        >
          {dateRange(entry.start_date, entry.end_date)}
        </Badge>
      </Table.Td>
      <Table.Td>
        {!isSlice && entry.allocations?.length > 0 ? (
          <Text size="xs" c="dimmed">
            {money(entry.allocated_amount)} dated ·{" "}
            {money(entry.unallocated_amount)} undated
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" truncate maw={220}>
          {entry.note || ""}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {!isSlice && (
            <Tooltip label="Date part of this revenue" withArrow>
              <ActionIcon
                variant="subtle"
                color="orange"
                aria-label="Add dated slice"
                onClick={() => {
                  setSlicingId(slicingId === entry.id ? null : entry.id);
                  setSlice(EMPTY_SLICE);
                }}
              >
                <IconScissors size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Delete" withArrow>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label="Delete"
              loading={busyId === entry.id}
              onClick={() => handleDelete(entry)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );

  const renderEntries = () => (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={700}>Recorded revenue</Text>
            <Text size="sm" c="dimmed">
              {totals.length === 0
                ? "Nothing recorded yet for this selling year."
                : `${totals.length} ${totals.length === 1 ? "rep" : "reps"} · ${money(
                    totals.reduce((sum, t) => sum + Number(t.revenue || 0), 0)
                  )} total`}
            </Text>
          </div>
        </Group>

        {loading ? (
          <Center py="lg">
            <Loader />
          </Center>
        ) : entries.length === 0 ? null : (
          <Table
            verticalSpacing="xs"
            highlightOnHover
            className="revenue-import-page-table"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Rep</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Tiers</Table.Th>
                <Table.Th>Accounts</Table.Th>
                <Table.Th>Dates</Table.Th>
                <Table.Th>Split</Table.Th>
                <Table.Th>Note</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map(entry => (
                <React.Fragment key={entry.id}>
                  {renderRow(entry)}
                  {(entry.allocations || []).map(a => renderRow(a, true))}
                  {slicingId === entry.id && renderSliceForm(entry)}
                </React.Fragment>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );

  return (
    <PageContent
      title="Import Revenue"
      subtitle="Record revenue for reps without a customer list, and date the parts that overrides depend on"
      breadcrumbs={[
        { label: "Rep Management", path: "/downline" },
        { label: "Import Revenue" },
      ]}
      headerActions={headerActions}
    >
      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {renderSteps()}
        {renderUploader()}
        {renderSingleEntry()}
        {renderEntries()}
      </Stack>
    </PageContent>
  );
}

export default RevenueImportPage;
